import React, { useState, useEffect, useRef } from "react";
import "./AgentStudio.css";
import ApiService from "../../services/Api.service";
import AuthService from "../../services/Auth.service";
import { chatWebSocket } from "../../services/chatWebSocket.service.js";
import toast from "react-hot-toast";
import AgentSidebar from "./AgentSidebar";
import AgentConfigTab from "./AgentConfigTab";
import ScriptConfigTab from "./ScriptConfigTab";
import LlmConfigTab from "./LlmConfigTab";
import AudioConfigTab from "./AudioConfigTab";
import CallConfigTab from "./CallConfigTab";
import AdvancedConfigTab from "./AdvancedConfigTab";
import ChatSimulator from "./ChatSimulator";
import TemplateCatalogModal from "./TemplateCatalogModal";
import Button from "../Button/Button.js";
import Tabs from "../Tabs/Tabs.js";
import Modal from "../Modal/Modal.js";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader.js";
import { normalizeSections } from "./ScriptFlow/scriptFlowUtils.js";
import {
  Bot,
  Plus,
  Phone,
  Sliders,
  Volume2,
  Cpu,
  FileText,
  ChevronRight,
  ChevronLeft,
  GripVertical,
  Save,
  MessageSquare
} from "lucide-react";

const STUDIO_TABS = ["Basic Details", "Script", "LLM", "Audio", "Call", "Advanced"];

const parseTimeStr = (timeVal, defaultHour, defaultMin, defaultAmPm) => {
  if (timeVal === undefined || timeVal === null || timeVal === "") {
    return { hour: defaultHour, minute: defaultMin, amPm: defaultAmPm };
  }
  
  // If timeVal is a number (seconds from midnight)
  if (typeof timeVal === "number") {
    try {
      let totalMinutes = Math.floor(timeVal / 60);
      let h = Math.floor(totalMinutes / 60);
      let m = totalMinutes % 60;
      let ampm = "AM";
      if (h >= 12) {
        ampm = "PM";
        if (h > 12) h -= 12;
      } else if (h === 0) {
        h = 12;
      }
      return {
        hour: String(h).padStart(2, "0"),
        minute: String(m).padStart(2, "0"),
        amPm: ampm
      };
    } catch (e) {
      console.warn("Error parsing numeric timeVal", timeVal, e);
    }
  }

  // If timeVal is a string (like "09:00:00")
  if (typeof timeVal === "string") {
    try {
      const parts = timeVal.split(":");
      if (parts.length >= 2) {
        let h = parseInt(parts[0], 10);
        const m = parts[1].slice(0, 2);
        let ampm = "AM";
        if (h >= 12) {
          ampm = "PM";
          if (h > 12) h -= 12;
        } else if (h === 0) {
          h = 12;
        }
        return {
          hour: String(h).padStart(2, "0"),
          minute: m,
          amPm: ampm
        };
      }
    } catch (e) {
      console.warn("Error parsing string timeVal", timeVal, e);
    }
  }

  return { hour: defaultHour, minute: defaultMin, amPm: defaultAmPm };
};

const normalizeUserDefinedPrompt = (rawPrompt, defaultPromptScript, defaultInitialMessage) => {
  const sectionTitleMap = {
    welcome_message: "Greeting (Opening)",
    mandatory_disclosures: "Mandatory (Compliance / Disclosures)",
    scenarios: "Scenarios",
    rebuttals_faqs: "Rebuttals / FAQs",
    final_call_message: "Closing (Call to Action)",
    more_info: "More Info"
  };

  const sectionDescMap = {
    welcome_message: "Welcome the customer and introduce the agent.",
    mandatory_disclosures: "Regulatory compliance, disclaimers & mandatory disclosures.",
    scenarios: "User dynamic details & extra use-case scenarios.",
    rebuttals_faqs: "Answer common questions and handle objections.",
    final_call_message: "Confirm the next action, thank the customer, and close the call.",
    more_info: "Extra resources, documentation links & follow-up data."
  };

  if (Array.isArray(rawPrompt) && rawPrompt.length > 0) {
    return rawPrompt.map(item => ({
      ...item,
      id: item.id || item.section_id || item.key || `custom_${Date.now()}`,
      title: item.title !== undefined ? item.title : (sectionTitleMap[item.id] || item.id || "Custom Section"),
      description: item.description !== undefined ? item.description : (sectionDescMap[item.id] || ""),
      prompt: item.prompt !== undefined ? item.prompt : "",
      variables: item.variables || { preprocessing_fields: {}, additional_fields: {} }
    }));
  }

  if (rawPrompt && typeof rawPrompt === "object" && !Array.isArray(rawPrompt)) {
    const defaultKeys = ["welcome_message", "conversation_flow", "special_cases", "rebuttals_faqs", "final_call_message"];
    const allKeys = Array.from(new Set([...defaultKeys, ...Object.keys(rawPrompt)]));

    const bodyKeys = allKeys.filter(k => k !== "final_call_message" && k !== "welcome_message");
    const orderedKeys = ["welcome_message", ...bodyKeys, "final_call_message"];

    return orderedKeys.map(key => {
      const obj = rawPrompt[key] || {};
      let pVal = obj.prompt !== undefined ? obj.prompt : "";
      if (key === "welcome_message" && !pVal) pVal = defaultInitialMessage || "";
      if (key === "conversation_flow" && !pVal) pVal = defaultPromptScript || "";

      return {
        ...obj,
        id: key,
        title: obj.title !== undefined ? obj.title : (sectionTitleMap[key] || key.replace(/_/g, " ").replace(/^custom_/, "").replace(/\b\w/g, l => l.toUpperCase())),
        description: obj.description !== undefined ? obj.description : (sectionDescMap[key] || "Custom script section guidelines and instructions."),
        prompt: pVal,
        variables: obj.variables || { preprocessing_fields: {}, additional_fields: {} }
      };
    });
  }

  return [
    {
      id: "welcome_message",
      title: sectionTitleMap.welcome_message,
      description: sectionDescMap.welcome_message,
      prompt: defaultInitialMessage || "",
      variables: { preprocessing_fields: {}, additional_fields: {} }
    },
    {
      id: "conversation_flow",
      title: sectionTitleMap.conversation_flow,
      description: sectionDescMap.conversation_flow,
      prompt: defaultPromptScript || "",
      variables: { preprocessing_fields: {}, additional_fields: {} }
    },
    {
      id: "special_cases",
      title: sectionTitleMap.special_cases,
      description: sectionDescMap.special_cases,
      prompt: "",
      variables: { preprocessing_fields: {}, additional_fields: {} }
    },
    {
      id: "rebuttals_faqs",
      title: sectionTitleMap.rebuttals_faqs,
      description: sectionDescMap.rebuttals_faqs,
      prompt: "",
      variables: { preprocessing_fields: {}, additional_fields: {} }
    },
    {
      id: "final_call_message",
      title: sectionTitleMap.final_call_message,
      description: sectionDescMap.final_call_message,
      prompt: "",
      variables: { preprocessing_fields: {}, additional_fields: {} }
    }
  ];
};

const normalizeAgentData = (raw) => {
  if (!raw) return null;

  const startParsed = parseTimeStr(raw.start_time, "09", "00", "AM");
  const endParsed = parseTimeStr(raw.end_time, "08", "00", "PM");

  const promptScript = raw.call_flow_script || (typeof raw.script === "string" && !raw.script.match(/^[0-9a-fA-F]{24}$/) ? raw.script : "") || "You are Zara, a helpful customer assistant. Greet the customer warmly and assist them with their queries.";
  const initialMessage = raw.initial_message || raw.welcome_message || raw.greeting || `Hello! Thanks for connecting with ${raw.company_name || "ValueEnable"}. How may I assist you regarding your ${raw.campaign_name || "policy"} today?`;

  const userDefinedPromptArray = normalizeUserDefinedPrompt(raw.prompt_sections, promptScript, initialMessage);

  const adv = raw.advance_settings || {};
  const holdLineVal = adv.hold_line !== undefined && adv.hold_line !== null ? Number(adv.hold_line) : (raw.hold_line !== undefined && raw.hold_line !== null ? Number(raw.hold_line) : 3);
  const timeoutVal = adv.timeout !== undefined && adv.timeout !== null ? Number(adv.timeout) : (raw.timeout !== undefined && raw.timeout !== null ? Number(raw.timeout) : 60);
  const langWordCountVal = adv.lang_switch_word_count !== undefined && adv.lang_switch_word_count !== null ? Number(adv.lang_switch_word_count) : (raw.lang_switch_word_count !== undefined && raw.lang_switch_word_count !== null ? Number(raw.lang_switch_word_count) : 2);
  const langThresholdVal = adv.lang_switch_threshold !== undefined && adv.lang_switch_threshold !== null ? Number(adv.lang_switch_threshold) : (raw.lang_switch_threshold !== undefined && raw.lang_switch_threshold !== null ? Number(raw.lang_switch_threshold) : 75);
  const welcomeDelayVal = adv.welcome_delay !== undefined && adv.welcome_delay !== null ? Number(adv.welcome_delay) : (raw.welcome_delay !== undefined && raw.welcome_delay !== null ? Number(raw.welcome_delay) : 400);
  const ignoreSpeechVal = adv.ignore_speech !== undefined ? Boolean(adv.ignore_speech) : Boolean(raw.ignore_speech);

  const sttSettings = raw.stt_settings || {};
  const ttsSettings = raw.tts_settings || {};
  const sttModelVal = raw.stt_model || sttSettings.model;
  const endpointingVal = raw.endpointing !== undefined ? Number(raw.endpointing) : (sttSettings.endpointing !== undefined ? Number(sttSettings.endpointing) : 100);

  return {
    ...raw,
    id: raw.id || raw._id || `agent-${Date.now()}`,
    campaign_name: raw.campaign_name || raw.name || raw.title || "Unnamed Agent",
    description: raw.campaign_description || raw.description || "",
    initial_message: initialMessage,
    gender: raw.gender || raw.voice_gender || "female",
    voice_gender: raw.voice_gender || raw.gender || "female",
    voice_character_id: raw.voice_character_id || raw.voice_id || "",
    tts_type: raw.tts_type || "Azure",
    stt_type: raw.stt_type || "Deepgram",
    ai_model: raw.llm_type || raw.ai_model || "GPT_4.1_nano",
    llm_type: raw.llm_type || raw.ai_model || "GPT_4.1_nano",
    language: raw.language || "Hinglish",
    language_mode: raw.language_mode || "default",
    dialer: raw.dialer || "Exotel",
    timeout: timeoutVal,
    numbers: Array.isArray(raw.numbers) ? raw.numbers : [],
    dispositions: Array.isArray(raw.dispositions) ? raw.dispositions : [],
    status: raw.status || "draft",
    hold_line: holdLineVal,
    lang_switch_word_count: langWordCountVal,
    lang_switch_threshold: langThresholdVal,
    welcome_delay: welcomeDelayVal,
    ignore_speech: ignoreSpeechVal,
    stt_settings: sttSettings,
    tts_settings: ttsSettings,
    stt_model: sttModelVal,
    endpointing: endpointingVal,
    temperature: raw.temperature !== undefined && raw.temperature !== null ? Number(raw.temperature) : 0.7,
    max_tokens: raw.max_tokens !== undefined && raw.max_tokens !== null ? Number(raw.max_tokens) : 250,
    top_p: raw.top_p !== undefined && raw.top_p !== null ? Number(raw.top_p) : 0.9,
    frequency_penalty: raw.frequency_penalty !== undefined && raw.frequency_penalty !== null ? Number(raw.frequency_penalty) : 0.0,
    presence_penalty: raw.presence_penalty !== undefined && raw.presence_penalty !== null ? Number(raw.presence_penalty) : 0.0,
    speech_speed: raw.speech_speed !== undefined && raw.speech_speed !== null ? Number(raw.speech_speed) : 1.0,
    call_transfer: Boolean(raw.call_transfer || raw.call_transfer_number || raw.transfer_enabled),
    transfer_enabled: Boolean(raw.call_transfer || raw.call_transfer_number || raw.transfer_enabled),
    transfer_number: raw.call_transfer_number || raw.transfer_number || "",
    call_transfer_number: raw.call_transfer_number || raw.transfer_number || "",
    call_transfer_threshold: raw.call_transfer_threshold !== undefined && raw.call_transfer_threshold !== null ? Number(raw.call_transfer_threshold) : (raw.transfer_threshold !== undefined ? Number(raw.transfer_threshold) : 0.7),
    transfer_threshold: raw.transfer_threshold !== undefined ? Number(raw.transfer_threshold) : (raw.call_transfer_threshold !== undefined && raw.call_transfer_threshold !== null ? Number(raw.call_transfer_threshold) : 0.7),
    start_hour: startParsed.hour,
    start_minute: startParsed.minute,
    start_am_pm: startParsed.amPm,
    end_hour: endParsed.hour,
    end_minute: endParsed.minute,
    end_am_pm: endParsed.amPm,
    call_flow_script: promptScript,
    prompt_sections: userDefinedPromptArray
  };
};

export default function AgentStudio() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("Basic Details");

  const workspaceContentRef = useRef(null);

  useEffect(() => {
    if (workspaceContentRef.current) {
      workspaceContentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);
  const [selectedAgent, setSelectedAgent] = useState();
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [wsStatus, setWsStatus] = useState("disconnected");
  const chatBottomRef = useRef(null);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDialerModal, setShowDialerModal] = useState(false);
  const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDirCollapsed, setIsDirCollapsed] = useState(false);
  const [isSimCollapsed, setIsSimCollapsed] = useState(false);
  const [dirWidth, setDirWidth] = useState(280);
  const [simWidth, setSimWidth] = useState(340);
  const [dialNumber, setDialNumber] = useState("");
  const [isDialing, setIsDialing] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef(null);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [voiceCharacters, setVoiceCharacters] = useState([]);
  const [llmModels, setLlmOptions] = useState([]);
  const [sttOptions, setSttOptions] = useState([]);
  const [sttModelOptions, setSttModelOptions] = useState([]);
  const [ttsOptions, setTtsOptions] = useState([]);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [dialerOptions, setDialerOptions] = useState([]);
  const [dispositionOptions, setDispositionOptions] = useState([]);

  const dragStartRef = useRef({ type: null, startX: 0, startWidth: 0 });

  const handleMouseDown = (e, type) => {
    e.preventDefault();
    dragStartRef.current = {
      type,
      startX: e.clientX,
      startWidth: type === "directory" ? dirWidth : simWidth
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const { type, startX, startWidth } = dragStartRef.current;
    if (!type) return;

    const deltaX = e.clientX - startX;
    if (type === "directory") {
      const newWidth = Math.max(200, Math.min(500, startWidth + deltaX));
      setDirWidth(newWidth);
    } else if (type === "simulator") {
      const newWidth = Math.max(280, Math.min(600, startWidth - deltaX));
      setSimWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    dragStartRef.current = { type: null, startX: 0, startWidth: 0 };
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const [collapsedSections, setCollapsedSections] = useState({
    greetingRules: false,
    modelPrompts: false,
    langSelection: false,
    voiceSelection: false,
    sttEngines: false,
    callSchedules: false,
    callTransfer: false
  });

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
 
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchAgents();
    loadApiOptions();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      setChatMessages([
        {
          id: "msg-welcome",
          sender: "agent",
          text: selectedAgent.initial_message || "Hello! How can I assist you today?",
          latency: 0
        }
      ]);
    }
  }, [selectedAgent?.id, selectedAgent?.initial_message]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isTyping]);

  useEffect(() => {
    if (!selectedAgent?.id) return;

    const isMock = String(selectedAgent.id).startsWith("mock-") || String(selectedAgent.id).startsWith("new-agent-");
    if (isMock) return;
  }, [selectedAgent?.id, selectedAgent?.script]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const res = await ApiService.getCampaignList({ page: 1, size: 20 });
      if (res?.data?.data && res.data.data.length > 0) {
        const normalizedList = res.data.data.map(normalizeAgentData);
        setAgents(normalizedList);
        
        const firstAgent = normalizedList[0];
        setSelectedAgent(firstAgent);
        if (!String(firstAgent.id).startsWith("mock-")) {
          try {
            const detailRes = await AuthService.getByobCampaign(firstAgent.id);
            const rawDetail = detailRes?.data?.data || detailRes?.data;
            if (rawDetail) {
              const freshFirst = normalizeAgentData({ ...firstAgent, ...rawDetail });
              setSelectedAgent(freshFirst);
              setAgents(prev => prev.map(a => a.id === freshFirst.id ? freshFirst : a));
            }
          } catch (err) {
            console.warn("Failed to fetch initial agent details", err);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load agents", err);
    } finally {
      setLoading(false);
    }
  };

  const loadApiOptions = async () => {
    try {
      const [langRes, ivrRes, voiceRes, dispRes] = await Promise.all([
        ApiService.getLanguageOptions().catch(() => null),
        ApiService.getIvrOptions({}).catch(() => null),
        ApiService.getVoiceCharacters().catch(() => null),
        ApiService.getDispositionListOptions().catch(() => null)
      ]);

      if (langRes?.data) {
        const defaultLangs = langRes.data.default_language || [];
        const otherLangs = langRes.data.other_languages || [];
        const languages = [...defaultLangs, ...otherLangs];
        if (languages.length > 0) setLanguageOptions(languages);
      }
      
      if (ivrRes?.data) {
        const models = ivrRes.data.llm_options || ivrRes.data.models || [];
        if (models.length > 0) setLlmOptions(models);

        const dialers = ivrRes.data.dialer_options || [];
        if (dialers.length > 0) setDialerOptions(dialers);

        const stts = ivrRes.data.stt_options || [];
        if (stts.length > 0) setSttOptions(stts);

        const sttModels = ivrRes.data.stt_model_options || [];
        if (sttModels.length > 0) setSttModelOptions(sttModels);

        const ttss = ivrRes.data.tts_options || [];
        if (ttss.length > 0) setTtsOptions(ttss);

        const scripts = ivrRes.data.script_options || [];
        if (scripts.length > 0) setScriptOptions(scripts);
      }

      if (dispRes?.data) {
        let list = [];
        if (dispRes.data.dispositions && Array.isArray(dispRes.data.dispositions)) {
          list = dispRes.data.dispositions;
        } else if (Array.isArray(dispRes.data)) {
          list = dispRes.data;
        } else {
          list = dispRes.data.data || [];
        }
        if (list.length > 0) {
          setDispositionOptions(list.map(d => typeof d === "string" ? { label: d, value: d } : { label: d.label || d.name || d.value, value: d.value || d.id }));
        }
      }

      if (voiceRes?.data?.data && voiceRes.data.data.length > 0) {
        setVoiceCharacters(voiceRes.data.data);
      }
    } catch (err) {
      console.warn("Endpoints loading options caught minor issues.", err);
    }
  };

  useEffect(() => {
    const fetchDynamicOptions = async () => {
      if (!selectedAgent?.language) return;

      try {
        const ivrRes = await ApiService.getIvrOptions({
          language: selectedAgent.language,
          stt_type: selectedAgent.stt_type
        }).catch(() => null);
        if (ivrRes?.data) {
          const models = ivrRes.data.llm_options || ivrRes.data.models || [];
          if (models.length > 0) setLlmOptions(models);

          const dialers = ivrRes.data.dialer_options || [];
          if (dialers.length > 0) setDialerOptions(dialers);

          const stts = ivrRes.data.stt_options || [];
          if (stts.length > 0) setSttOptions(stts);

          const sttModels = ivrRes.data.stt_model_options || [];
          setSttModelOptions(sttModels);

          const ttss = ivrRes.data.tts_options || [];
          if (ttss.length > 0) setTtsOptions(ttss);

          const scripts = ivrRes.data.script_options || [];
          if (scripts.length > 0) setScriptOptions(scripts);
        }

        const voiceRes = await ApiService.getVoiceCharacterOptions({
          language: selectedAgent.language,
          gender: selectedAgent.gender
        }).catch(() => null);
        if (voiceRes?.data?.data) {
          setVoiceCharacters(voiceRes.data.data);
        }
      } catch (err) {
        console.warn("Dynamic options loading failed", err);
      }
    };
    fetchDynamicOptions();
  }, [selectedAgent?.language, selectedAgent?.gender, selectedAgent?.stt_type]);

  useEffect(() => {
    if (!selectedAgent) return;
    if (selectedAgent.language && !languageOptions.some(l => (typeof l === "string" ? l : l.value) === selectedAgent.language)) {
      setLanguageOptions(prev => [...prev, { value: selectedAgent.language, label: selectedAgent.language }]);
    }
    
    if (selectedAgent.ai_model && !llmModels.some(m => (typeof m === "string" ? m : m.value || m.id) === selectedAgent.ai_model)) {
      setLlmOptions(prev => [...prev, selectedAgent.ai_model]);
    }

    if (selectedAgent.dialer && !dialerOptions.some(d => String(d.value || d) === String(selectedAgent.dialer))) {
      setDialerOptions(prev => [...prev, { label: selectedAgent.dialer, value: selectedAgent.dialer }]);
    }

    if (selectedAgent.stt_type && !sttOptions.some(s => String(s.value || s) === String(selectedAgent.stt_type))) {
      setSttOptions(prev => [...prev, { label: selectedAgent.stt_type, value: selectedAgent.stt_type }]);
    }
    
    if (selectedAgent.voice_character_id && !voiceCharacters.some(v => v.id === selectedAgent.voice_character_id)) {
      setVoiceCharacters(prev => [...prev]);
    }
  }, [selectedAgent?.language, selectedAgent?.ai_model, selectedAgent?.voice_character_id]);


  const handleSelectAgent = async (agent) => {
    const initialNormalized = normalizeAgentData(agent);
    setSelectedAgent(initialNormalized);
    setActiveTab("Basic Details");
    toast.success(`Swapped to workspace: ${initialNormalized.campaign_name}`, { id: "swapped-agent" });

    const isMock = String(agent.id).startsWith("mock-") || String(agent.id).startsWith("new-agent-");
    if (!isMock) {
      try {
        let detailRes;
          detailRes = await AuthService.getByobCampaign(agent.id);
         const rawDetail = detailRes?.data?.data || detailRes?.data;
        if (rawDetail) {
          const freshNormalized = normalizeAgentData({ ...agent, ...rawDetail });
          setSelectedAgent(freshNormalized);
          setAgents(prev => prev.map(a => a.id === freshNormalized.id ? freshNormalized : a));
        }
      } catch (err) {
        console.warn("Failed to fetch fresh details of campaign", agent.id, err);
      }
    }
  };

  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormErrors(prev => {
      const copy = { ...prev };
      delete copy[field];
      if (field === "prompt_sections") {
        delete copy.welcome_message;
        delete copy.final_call_message;
      }
      return copy;
    });

    if (field.startsWith("prompt_sections.")) {
      const parts = field.split(".");
      setSelectedAgent(prev => {
        const copy = { ...prev };
        let current = copy;
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          current[part] = { ...current[part] };
          current = current[part];
        }
        current[parts[parts.length - 1]] = value;
        return copy;
      });
    } else {
      setSelectedAgent(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveAgent = async () => {
    if (!selectedAgent) return;

    // Validate required fields
    const errors = {};
    if (!selectedAgent.campaign_name?.trim()) {
      errors.campaign_name = "Agent Name is required";
    }
    if (!selectedAgent.ai_model?.trim() && !selectedAgent.llm_type?.trim()) {
      errors.ai_model = "AI Model Engine is required";
    }
    if (!selectedAgent.language?.trim()) {
      errors.language = "Spoken Language is required";
    }
    if (!selectedAgent.stt_type?.trim()) {
      errors.stt_type = "STT Provider Engine is required";
    }
    if (!selectedAgent.stt_model?.trim() && !selectedAgent.stt_settings?.model?.trim()) {
      errors.stt_model = "STT Model is required";
    }
    if (!selectedAgent.tts_type?.trim() && !selectedAgent.voice_character_id) {
      errors.tts_type = "TTS Provider Engine is required";
    }
    if (!selectedAgent.dialer?.trim()) {
      errors.dialer = "Telephony Dialer is required";
    }
    if (selectedAgent.transfer_enabled && !selectedAgent.transfer_number?.trim()) {
      errors.transfer_number = "Transfer Destination Number is required when call transfer is enabled";
    }

    // Validate Script Section required steps (Greeting & Closing)
    const scriptSections = normalizeSections(selectedAgent);

    const welcomeSec = scriptSections.find(s => s.id === "welcome_message");
    if (!welcomeSec?.prompt?.trim()) {
      errors.welcome_message = "Greeting (Opening) script message is required";
    }

    const closingSec = scriptSections.find(s => s.id === "final_call_message");
    if (!closingSec?.prompt?.trim()) {
      errors.final_call_message = "Closing (Call to Action) script message is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      
      const fieldTabMap = {
        campaign_name: "Basic Details",
        welcome_message: "Script",
        final_call_message: "Script",
        ai_model: "LLM",
        language: "Audio",
        stt_type: "Audio",
        stt_model: "Audio",
        tts_type: "Audio",
        dialer: "Call",
        transfer_number: "Call"
      };

      const tabErrorMap = {};
      Object.keys(errors).forEach(f => {
        const tab = fieldTabMap[f] || "Basic Details";
        if (!tabErrorMap[tab]) tabErrorMap[tab] = [];
        tabErrorMap[tab].push(errors[f]);
      });

      let targetTab = activeTab;
      if (!tabErrorMap[activeTab]) {
        const tabOrder = ["Basic Details", "Script", "LLM", "Audio", "Call"];
        targetTab = tabOrder.find(t => tabErrorMap[t]) || Object.keys(tabErrorMap)[0];
      }

      setActiveTab(targetTab);
      const firstErrorMsg = tabErrorMap[targetTab]?.[0] || Object.values(errors)[0];
      toast.error(`Error in [${targetTab}] tab: ${firstErrorMsg}`);
      return false;
    }
    setFormErrors({});

    const toastId = toast.loading("Saving agent parameters to database...");
    try {
      const convertTo24Hour = (hour, minute, ampm) => {
        let h = parseInt(hour, 10) || 0;
        const m = String(minute || "00").padStart(2, "0");
        if (ampm === "PM" && h < 12) h += 12;
        if (ampm === "AM" && h === 12) h = 0;
        return `${String(h).padStart(2, "0")}:${m}:00`;
      };
      const voiceChar = voiceCharacters.find(v => v.id === selectedAgent.voice_character_id);
      const ttsType = selectedAgent.tts_type || voiceChar?.tts_type ;

      const companyId = selectedAgent.company_id || localStorage.getItem("company_id") || "691590934fe368874a318642";
      const companyName = selectedAgent.company_name || localStorage.getItem("company_name") || "ValuEnable";

      const promptSections = normalizeSections(selectedAgent);
      const welcomeSec = promptSections.find(s => s.id === "welcome_message");
      const closingSec = promptSections.find(s => s.id === "final_call_message");

      const byobPayload = {
        campaign_name: selectedAgent.campaign_name || "My New Agent",
        campaign_description: selectedAgent.description || selectedAgent.campaign_description || "User Generated BYOB Agent",
        start_time: convertTo24Hour(selectedAgent.start_hour, selectedAgent.start_minute, selectedAgent.start_am_pm),
        end_time: convertTo24Hour(selectedAgent.end_hour, selectedAgent.end_minute, selectedAgent.end_am_pm),
        dialer: selectedAgent.dialer || "TATA",
        stt_type: selectedAgent.stt_type || "deepgram",
        llm_type: selectedAgent.ai_model || selectedAgent.llm_type || "4.1_nano",
        tts_type: ttsType || selectedAgent.tts_type || "deepgram",
        voice_character_id: selectedAgent.voice_character_id || "char-1",
        script_id: selectedAgent.script_id || null,
        timeout: Number(selectedAgent.timeout) || 60,
        language: selectedAgent.language || "Hinglish",
        language_mode: selectedAgent.language_mode || "default",
        dispositions: selectedAgent.dispositions || [],
        prompt_sections: promptSections,
        welcome_message: welcomeSec?.prompt || "",
        final_call_message: closingSec?.prompt || "",
        company_id: companyId,
        company_name: companyName,
        status: selectedAgent.status || "active",
        numbers: selectedAgent.numbers || [],
        call_transfer: Boolean(selectedAgent.transfer_enabled || selectedAgent.call_transfer),
        call_transfer_number: (selectedAgent.transfer_enabled || selectedAgent.call_transfer) ? (selectedAgent.transfer_number || selectedAgent.call_transfer_number || "") : "",
        call_transfer_threshold: selectedAgent.transfer_threshold !== undefined ? Number(selectedAgent.transfer_threshold) : (selectedAgent.call_transfer_threshold !== undefined ? Number(selectedAgent.call_transfer_threshold) : 0.7),
        llm_settings: {
          ...(selectedAgent.llm_settings || {}),
          temperature: selectedAgent.temperature !== undefined ? Number(selectedAgent.temperature) : 0.5,
          top_p: selectedAgent.top_p !== undefined ? Number(selectedAgent.top_p) : 0.5,
          max_tokens: selectedAgent.max_tokens !== undefined ? Number(selectedAgent.max_tokens) : 500
        },
        stt_settings: {
          ...(selectedAgent.stt_settings || {}),
          model: selectedAgent.stt_model || selectedAgent.stt_settings?.model,
          language: selectedAgent.language || "hi",
          endpointing: Number(selectedAgent.endpointing) || 100
        },
        tts_settings: {
          ...(selectedAgent.tts_settings || {}),
          model: selectedAgent.voice_character_id || selectedAgent.tts_settings?.model,
          language: selectedAgent.language || "hi"
        },
        advance_settings: {
          hold_line: selectedAgent.hold_line !== undefined ? Number(selectedAgent.hold_line) : 3,
          timeout: selectedAgent.timeout !== undefined ? Number(selectedAgent.timeout) : 60,
          lang_switch_word_count: selectedAgent.lang_switch_word_count !== undefined ? Number(selectedAgent.lang_switch_word_count) : 2,
          lang_switch_threshold: selectedAgent.lang_switch_threshold !== undefined ? Number(selectedAgent.lang_switch_threshold) : 75
        }
      };

      const isNewDraft = String(selectedAgent.id).startsWith("mock-") || String(selectedAgent.id).startsWith("new-agent-");

      let res;
      if (isNewDraft) {
        res = await AuthService.byobCreateCampaign(byobPayload);
        if (res.status === 200 || res.status === 201) {
          toast.success("Build Your Own Bot Agent created successfully!", { id: toastId });
          await fetchAgents();
          return true;
        } else {
          const detail = res.data?.detail || res.data?.message || "Failed to create agent";
          throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
      } else {
        res = await AuthService.updateByobCampaign(selectedAgent.id, byobPayload);
        if (res.status === 200 || res.status === 201) {
          toast.success("BYOB Agent updated successfully!", { id: toastId });
          await fetchAgents();
          return true;
        } else {
          const detail = res.data?.detail || res.data?.message || "Failed to update agent";
          throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
      }
    } catch (err) {
      console.error("Error saving agent parameters:", err);
      let errMsg = "Failed to save agent parameters";
      const data = err?.response?.data;
      if (data) {
        if (typeof data === "string") {
          errMsg = data;
        } else if (data.detail) {
          if (typeof data.detail === "string") {
            errMsg = data.detail;
          } else if (Array.isArray(data.detail)) {
            errMsg = data.detail.map(d => d.msg || d.detail || JSON.stringify(d)).join("; ");
          } else {
            errMsg = data.detail.msg || JSON.stringify(data.detail);
          }
        } else if (data.message) {
          errMsg = typeof data.message === "string" ? data.message : JSON.stringify(data.message);
        } else if (data.error) {
          errMsg = typeof data.error === "string" ? data.error : JSON.stringify(data.error);
        }
      } else if (err?.message) {
        errMsg = err.message;
      }

      toast.error(errMsg, { id: toastId });
      return false;
    }
  };

  const handleOpenChatSimulator = async () => {
    const isNewDraft = !selectedAgent?.id || String(selectedAgent?.id).startsWith("mock-") || String(selectedAgent?.id).startsWith("new-agent-");

    if (isNewDraft) {
      toast("Please save the agent first to enable Chat Simulator.", { icon: "💾" });
      const saved = await handleSaveAgent();
      if (!saved) return false;
    }

    setChatMessages([]);

    chatWebSocket.connect(selectedAgent.id, {
      onStatusChange: (status) => setWsStatus(status),
      onOpen: () => {
        toast.success("Connected to live agent session!", { id: "ws-conn" });
      },
      onMessage: ({ text, latency }) => {
        setIsTyping(false);
        setChatMessages(prev => [
          ...prev,
          {
            id: `msg-agent-${Date.now()}`,
            sender: "agent",
            text,
            latency
          }
        ]);
      },
      onError: () => {
        setIsTyping(false);
        toast.error("WebSocket error on agent session", { id: "ws-err" });
      },
      onClose: () => {
        setIsTyping(false);
      }
    });

    return true;
  };

  const handleOpenDialerModal = async () => {
    const isNewDraft = !selectedAgent?.id || String(selectedAgent?.id).startsWith("mock-") || String(selectedAgent?.id).startsWith("new-agent-");

    if (isNewDraft) {
      toast("Please save the agent first to enable Live Test Calls.", { icon: "💾" });
      const saved = await handleSaveAgent();
      if (!saved) return;
    }

    setShowDialerModal(true);
  };


  const handleDeleteAgent = async (agentTarget) => {
    const target = agentTarget || selectedAgent;
    if (!target) return;

    if (window.confirm(`Are you sure you want to delete ${target.campaign_name}?`)) {
      const isNewDraft = String(target.id).startsWith("mock-") || String(target.id).startsWith("new-agent-");

      if (isNewDraft) {
        const remaining = agents.filter(a => a.id !== target.id);
        setAgents(remaining);
        if (remaining.length > 0 && selectedAgent?.id === target.id) {
          setSelectedAgent(remaining[0]);
        }
        toast.success("Draft deleted successfully");
      } else {
        const toastId = toast.loading("Deleting agent from database...");
        try {
          const res = await AuthService.deleteCampaign(target.id);
          if (res.status === 200 || res.status === 204) {
            toast.success("Agent deleted successfully from database!", { id: toastId });
            await fetchAgents();
          } else {
            throw new Error("Failed to delete agent");
          }
        } catch (err) {
          console.error("Error deleting agent:", err);
          const errMsg = err?.response?.data?.message || err?.message || "Failed to delete agent";
          toast.error(`Error: ${errMsg}`, { id: toastId });
        }
      }
    }
  };

  const handleSelectTemplate = (tpl) => {
    setShowTemplateModal(false);
    setShowCreateModal(false);

    const newAgentObj = {
      id: `new-agent-${Date.now()}`,
      campaign_name: tpl.title,
      description: tpl.description || "Created from agent template.",
      bot_type: "call",
      initial_message: tpl.initial_message,
      call_flow_script: tpl.prompt_sections?.conversation_flow?.prompt || "",
      prompt_sections: tpl.prompt_sections,
      language: tpl.language || "Hinglish",
      gender: tpl.gender || "female",
      voice_character_id: tpl.voice_character_id || "char-1",
      timeout: 60,
      ai_model: "GPT_4.1_nano",
      status: "draft",
      start_hour: "09", start_minute: "00", start_am_pm: "AM",
      end_hour: "08", end_minute: "00", end_am_pm: "PM",
      temperature: 0.7,
      speech_speed: 1.0
    };

    setAgents(prev => [newAgentObj, ...prev]);
    setSelectedAgent(newAgentObj);
    toast.success(`Created agent from template: ${tpl.title}`);
  };

  const handleCreateAgent = (choice) => {
    setShowCreateModal(false);
    let newName = "My New Agent";
    let newLang = "Hinglish";
    let newCharId = "char-1";

    if (choice === "template-support") {
      newName = "Tier-1 Customer Support";
    } else if (choice === "template-sales") {
      newName = "Outbound Lead Gen Agent";
      newLang = "Hinglish";
      newCharId = "char-5";
    }

    const chosenPrompt = choice === "template-sales" 
      ? "You are Aditi, an energetic lead-generation executive..." 
      : "You are a customer support agent. Resolve issues step by step...";

    const newAgentObj = {
      id: `new-agent-${Date.now()}`,
      campaign_name: newName,
      description: "Auto-generated templates config. Edit below parameters to fine-tune.",
      bot_type: "call",
      initial_message: choice === "template-sales" 
        ? "Hello! Mera naam Aditi hai ValueEnable se. Kya aap automated IVR sales setup ke baare me discuss karna chahenge?"
        : "Hi, thanks for calling ValueEnable Support. How can I resolve your dashboard query today?",
      call_flow_script: chosenPrompt,
      prompt_sections: {
        conversation_flow: {
          prompt: chosenPrompt,
          variables: {
            preprocessing_fields: {},
            additional_fields: {}
          }
        },
        special_cases: {
          prompt: "",
          variables: {
            preprocessing_fields: {},
            additional_fields: {}
          }
        },
        rebuttals_faqs: {
          prompt: "",
          variables: {
            preprocessing_fields: {},
            additional_fields: {}
          }
        },
        final_call_message: {
          prompt: "",
          variables: {
            preprocessing_fields: {},
            additional_fields: {}
          }
        }
      },
      language: newLang,
      gender: "female",
      voice_character_id: newCharId,
      timeout: 60,
      ai_model: "GPT_4.1_nano",
      status: "draft",
      start_hour: "09", start_minute: "00", start_am_pm: "AM",
      end_hour: "08", end_minute: "00", end_am_pm: "PM",
      temperature: 0.7,
      speech_speed: 1.0
    };

    setAgents(prev => [newAgentObj, ...prev]);
    setSelectedAgent(newAgentObj);
    toast.success(`Created agent: ${newAgentObj.campaign_name}`);
  };

  const handlePlayVoice = async (e, char) => {
    e.stopPropagation();

    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    if (playingVoiceId === char.id) {
      setPlayingVoiceId(null);
      setAudioElement(null);
      return;
    }
    try {
      setPlayingVoiceId(char.id);
      const res = await ApiService.playVoiceCharacter(char.id);
      
      if (res?.data?.signed_url || res?.data?.audio_url) {
        const audioUrl = res.data.signed_url || res.data.audio_url;
        const audio = new Audio(audioUrl);
        audio.onended = () => {
          setPlayingVoiceId(null);
          setAudioElement(null);
        };
        audio.onerror = () => {
          toast.error("Failed to play voice sample");
          setPlayingVoiceId(null);
          setAudioElement(null);
        };
        setAudioElement(audio);
        await audio.play();
      }
    } catch (err) {
      setPlayingVoiceId(null);
    }
  };



  const handleSendChatMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !selectedAgent?.id) return;

    const textToSend = chatInput.trim();
    const userMsg = {
      id: `msg-user-${Date.now()}`,
      sender: "user",
      text: textToSend,
      latency: 0
    };

    setChatMessages(prev => [...prev, userMsg]);
    setChatInput("");
    setIsTyping(true);

    const sent = chatWebSocket.sendMessage(textToSend, selectedAgent.id);
    if (!sent) {
      setIsTyping(false);
      toast.error("Failed to send message: WebSocket is disconnected.");
    }
  };

  const handleEndChatSession = () => {
    chatWebSocket.disconnect();
    setWsStatus("disconnected");
  };

  const handleReconnectChatSession = () => {
    if (selectedAgent?.id) {
      handleOpenChatSimulator();
    }
  };

  useEffect(() => {
    return () => {
      chatWebSocket.disconnect();
    };
  }, [selectedAgent?.id]);

  const handleDialCall = (e) => {
    e.preventDefault();
    if (!dialNumber.trim()) return;

    setIsDialing(true);
    const toastId = toast.loading(`Triggering live test call to ${dialNumber}...`);

    setTimeout(() => {
      setIsDialing(false);
      setShowDialerModal(false);
      toast.success("Simulated Call Triggered! Zarra is dialling your terminal.", { id: toastId });
    }, 2000);
  };

  
  const currentTabIndex = STUDIO_TABS.indexOf(activeTab) >= 0 ? STUDIO_TABS.indexOf(activeTab) : 0;
  const hasPrevious = currentTabIndex > 0;
  const hasNext = currentTabIndex < STUDIO_TABS.length - 1;

  const handlePrevTab = () => {
    if (hasPrevious) {
      setActiveTab(STUDIO_TABS[currentTabIndex - 1]);
    }
  };

  const handleNextTab = () => {
    if (hasNext) {
      setActiveTab(STUDIO_TABS[currentTabIndex + 1]);
    } else {
      handleSaveAgent();
    }
  };

  return (
    <div className={`agent-studio-container ${isFullscreen ? "fullscreen-studio" : ""}`}>
      {!isDirCollapsed && (
        <AgentSidebar
          agents={agents}
          selectedAgent={selectedAgent}
          loading={loading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          setShowCreateModal={setShowCreateModal}
          handleSelectAgent={handleSelectAgent}
          handleDeleteAgent={handleDeleteAgent}
          handleCreateAgent={handleCreateAgent}
          dirWidth={dirWidth}
          handleMouseDown={handleMouseDown}
          isDirCollapsed={isDirCollapsed}
          setIsDirCollapsed={setIsDirCollapsed}
        />
      )}

      {!isDirCollapsed && (
        <div 
          className="studio-resize-handle" 
          onMouseDown={(e) => handleMouseDown(e, "directory")}
          title="Drag to resize directory"
        >
          <div className="resize-grip-icon-wrapper">
            <GripVertical size={12} />
          </div>
        </div>
      )}

      <main className="studio-workspace-panel">
        <div className="workspace-header" style={{ paddingBottom: "6px" }}>
          <div className="workspace-title-row" style={{ marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {isDirCollapsed && (
                <button
                  className="btn-import-agent"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    marginRight: "6px"
                  }}
                  onClick={() => setIsDirCollapsed(false)}
                  title="Expand Sidebar"
                >
                  <ChevronRight size={14} />
                </button>
              )}
              <span style={{ fontSize: "17px", fontWeight: 700, color: "var(--slate-800, #1e293b)" }}>
                {selectedAgent?.campaign_name || "Unnamed Agent"}
              </span>
            </div>

            {/* Header Action: Save Agent Button */}
            <Button variant="primary" size="sm" onClick={handleSaveAgent} style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 600 }}>
              <Save size={14} /> Save Agent
            </Button>
          </div>

          {/* Config tabs bar */}
          {(() => {
            const checkTabError = (tabId) => {
              if (!formErrors || Object.keys(formErrors).length === 0) return false;
              const map = {
                "Basic Details": ["campaign_name"],
                "Script": ["welcome_message", "final_call_message"],
                "LLM": ["ai_model"],
                "Audio": ["language", "stt_type", "stt_model", "tts_type"],
                "Call": ["dialer", "transfer_number"]
              };
              const fields = map[tabId] || [];
              return fields.some(f => Boolean(formErrors[f]));
            };

            return (
              <Tabs
                tabs={[
                  { id: "Basic Details", label: "Basic Details", icon: <Bot size={14} />, hasError: checkTabError("Basic Details") },
                  { id: "Script", label: "Script", icon: <FileText size={14} />, hasError: checkTabError("Script") },
                  { id: "LLM", label: "LLM", icon: <Cpu size={14} />, hasError: checkTabError("LLM") },
                  { id: "Audio", label: "Audio", icon: <Volume2 size={14} />, hasError: checkTabError("Audio") },
                  { id: "Call", label: "Call", icon: <Phone size={14} />, hasError: checkTabError("Call") },
                  { id: "Advanced", label: "Advanced", icon: <Sliders size={14} /> }
                ]}
                activeTab={activeTab}
                onChange={setActiveTab}
                variant="line"
                color="secondary"
                className="workspace-tabs-bar"
              />
            );
          })()}
        </div>

        <div className="workspace-content-area" ref={workspaceContentRef}>
          {loading ? (
            <div style={{ padding: "20px" }}>
              <SkeletonLoader type="form" count={4} />
            </div>
          ) : (
            <>
              {activeTab === "Basic Details" && (
                <AgentConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                  formErrors={formErrors}
                />
              )}

              {activeTab === "Script" && (
                <ScriptConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                  formErrors={formErrors}
                />
              )}

              {activeTab === "LLM" && (
                <LlmConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                  collapsedSections={collapsedSections}
                  toggleSection={toggleSection}
                  llmModels={llmModels}
                  handleOpenScriptBuilder={() => setIsScriptModalOpen(true)}
                  formErrors={formErrors}
                />
              )}

              {activeTab === "Audio" && (
                <AudioConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                  collapsedSections={collapsedSections}
                  toggleSection={toggleSection}
                  languageOptions={languageOptions}
                  voiceCharacters={voiceCharacters}
                  playingVoiceId={playingVoiceId}
                  handlePlayVoice={handlePlayVoice}
                  sttOptions={sttOptions}
                  sttModelOptions={sttModelOptions}
                  ttsOptions={ttsOptions}
                  formErrors={formErrors}
                />
              )}

              {activeTab === "Call" && (
                <CallConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                  collapsedSections={collapsedSections}
                  toggleSection={toggleSection}
                  dialerOptions={dialerOptions}
                  dispositionOptions={dispositionOptions}
                  formErrors={formErrors}
                />
              )}

              {activeTab === "Advanced" && (
                <AdvancedConfigTab
                  selectedAgent={selectedAgent}
                  handleInputChange={handleInputChange}
                />
              )}

            </>
          )}
        </div>
      </main>

      {!isSimCollapsed ? (
        <>
          <div 
            className="studio-resize-handle" 
            onMouseDown={(e) => handleMouseDown(e, "simulator")}
            title="Drag to resize simulator"
          >
            <div className="resize-grip-icon-wrapper">
              <GripVertical size={12} />
            </div>
          </div>

          <ChatSimulator
            selectedAgent={selectedAgent}
            chatMessages={chatMessages}
            isTyping={isTyping}
            chatInput={chatInput}
            setChatInput={setChatInput}
            handleSendChatMessage={handleSendChatMessage}
            handleOpenChatSimulator={handleOpenChatSimulator}
            handleOpenDialerModal={handleOpenDialerModal}
            chatBottomRef={chatBottomRef}
            simWidth={simWidth}
            connectionStatus={wsStatus}
            onEndChat={handleEndChatSession}
            onReconnectChat={handleReconnectChatSession}
            handleMouseDown={handleMouseDown}
            onCloseSimulator={() => setIsSimCollapsed(true)}
          />
        </>
      ) : (
        <div 
          className="studio-simulator-strip"
          onClick={() => setIsSimCollapsed(false)}
          title="Expand Chat with Agent"
        >
          <button type="button" className="sim-strip-toggle-btn" title="Expand Panel">
            <ChevronLeft size={15} />
          </button>
          <div className="sim-strip-label">
            <MessageSquare size={13} style={{ transform: "rotate(90deg)" }} />
            <span>Chat with Agent</span>
          </div>
        </div>
      )}

      {/* Create Voice Agent Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Voice Agent"
        subtitle="Select how you'd like to build your AI agent"
        icon={<Bot size={20} style={{ color: "#2563eb" }} />}
        maxWidth="520px"
      >
        <div className="modal-content-cards">
          <div className="modal-cards-row">
            <div className="creation-choice-card" onClick={() => handleCreateAgent("scratch")}>
              <div className="choice-icon-wrap scratch">
                <Plus size={20} />
              </div>
              <h4 className="choice-headline">Start From Scratch</h4>
              <p className="choice-subhead">Setup raw parameters, and build conversation prompts from ground up.</p>
            </div>
            {/* <div
              className="creation-choice-card"
              onClick={() => {
                setShowCreateModal(false);
                setShowTemplateModal(true);
              }}
            >
              <div className="choice-icon-wrap template">
                <Bot size={20} />
              </div>
              <h4 className="choice-headline">Start With Template</h4>
              <p className="choice-subhead">Deploy preconfigured, industry tested sales, support & recovery templates.</p>
            </div> */}
          </div>
        </div>
      </Modal>

      {/* Template Catalog Modal Dialog */}
      <TemplateCatalogModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Trigger Simulated Test Call Modal */}
      <Modal
        isOpen={showDialerModal}
        onClose={() => setShowDialerModal(false)}
        title="Trigger Simulated Test Call"
        subtitle="Test your voice agent live over SIP call"
        icon={<Phone size={20} style={{ color: "#2563eb" }} />}
        maxWidth="420px"
      >
        <form className="dialer-modal-form" onSubmit={handleDialCall}>
          <div className="dialer-field-group" style={{ marginBottom: 18 }}>
            <label className="field-label" style={{ marginBottom: 8, display: "block" }}>Enter Mobile Number</label>
            <input
              type="tel"
              required
              placeholder="+91 XXXXX XXXXX"
              className="field-input-textarea"
              style={{ height: 42, width: "100%", padding: "0 12px", boxSizing: "border-box" }}
              value={dialNumber}
              onChange={(e) => setDialNumber(e.target.value)}
            />
            <div className="field-helper" style={{ marginTop: 6 }}>
              Our SIP trunks will establish a connection to speak with Zara directly.
            </div>
          </div>
          <Button type="submit" variant="primary" size="lg" className="btn-dial-trigger" disabled={isDialing} style={{ width: "100%" }}>
            {isDialing ? "Triggering..." : "Simulate Call Now"}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
